const ERROR_ENDPOINT = '/api/site/error-report/client';
const DEDUPE_WINDOW_MS = 10000;
const recentFingerprints = new Map();

const normalizeString = (value) => {
    if (typeof value !== 'string') {
        return '';
    }

    return value.trim();
};

const resolveRequestVerificationToken = () => {
    let token = '';
    if (window.__litium && window.__litium.requestVerificationToken) {
        token = window.__litium.requestVerificationToken;
    }

    if (!token) {
        const tokenInput = document.querySelector('input[name="__RequestVerificationToken"]');
        if (tokenInput && tokenInput.value) {
            token = tokenInput.value;
        }
    }

    return token;
};

const cleanupFingerprints = () => {
    const now = Date.now();
    recentFingerprints.forEach((timestamp, fingerprint) => {
        if (now - timestamp > DEDUPE_WINDOW_MS) {
            recentFingerprints.delete(fingerprint);
        }
    });
};

const shouldReport = (payload) => {
    cleanupFingerprints();
    const fingerprint = JSON.stringify([
        payload.kind || '',
        payload.errorType || '',
        payload.message || '',
        payload.source || '',
        payload.line || 0,
        payload.column || 0,
        payload.status || 0,
        payload.requestUrl || '',
        payload.pageUrl || '',
    ]);

    const previous = recentFingerprints.get(fingerprint);
    const now = Date.now();
    if (previous && now - previous < DEDUPE_WINDOW_MS) {
        return false;
    }

    recentFingerprints.set(fingerprint, now);
    return true;
};

const buildHeaders = () => {
    const headers = {
        'content-type': 'application/json',
    };

    if (window.__litium && window.__litium.requestContext) {
        headers['litium-request-context'] = JSON.stringify(window.__litium.requestContext);
    }

    const token = resolveRequestVerificationToken();
    if (token) {
        headers.RequestVerificationToken = token;
    }

    return headers;
};

const postPayload = (payload) => {
    if (!shouldReport(payload)) {
        return Promise.resolve(false);
    }

    return window.fetch(ERROR_ENDPOINT, {
        method: 'POST',
        credentials: 'include',
        keepalive: true,
        headers: buildHeaders(),
        body: JSON.stringify(payload),
    }).then(() => true).catch(() => false);
};

const stringifyReason = (reason) => {
    if (!reason) {
        return '';
    }

    if (typeof reason === 'string') {
        return reason;
    }

    if (reason instanceof Error) {
        return reason.message || reason.toString();
    }

    try {
        return JSON.stringify(reason);
    } catch (error) {
        return String(reason);
    }
};

export const reportClientError = (payload) => {
    if (!payload || typeof window === 'undefined' || typeof window.fetch !== 'function') {
        return Promise.resolve(false);
    }

    return postPayload({
        severity: 'error',
        occurredAtUtc: new Date().toISOString(),
        pageUrl: window.location ? window.location.href : '',
        referrer: document.referrer || '',
        userAgent: navigator.userAgent || '',
        language: navigator.language || '',
        ...payload,
    });
};

export const reportHttpError = ({ url, method, status, statusText }) => {
    const normalizedUrl = normalizeString(url);
    if (normalizedUrl && normalizedUrl.indexOf(ERROR_ENDPOINT) >= 0) {
        return Promise.resolve(false);
    }

    return reportClientError({
        kind: 'http',
        errorType: 'HttpRequestFailure',
        title: status ? `HTTP request failed (${status})` : 'HTTP request failed',
        message: normalizeString(statusText) || `HTTP request failed for ${normalizedUrl || 'unknown url'}`,
        status,
        statusText,
        method,
        requestUrl: normalizedUrl,
        requestPath: normalizedUrl,
    });
};

const handleWindowError = (event) => {
    if (!event) {
        return;
    }

    const target = event.target || event.srcElement;
    if (target && target !== window && target !== document) {
        const sourceUrl = normalizeString(target.currentSrc || target.src || target.href);
        reportClientError({
            kind: 'resource',
            errorType: 'ResourceLoadError',
            title: 'Browser resource load failed',
            message: sourceUrl
                ? `Failed to load resource: ${sourceUrl}`
                : 'Failed to load browser resource',
            source: sourceUrl,
            meta: {
                tagName: target.tagName || '',
            },
        });
        return;
    }

    reportClientError({
        kind: 'javascript',
        errorType: normalizeString(event.error && event.error.name) || 'BrowserError',
        title: 'Unhandled browser error',
        message: normalizeString(event.message) ||
            normalizeString(event.error && event.error.message) ||
            'Unhandled browser error',
        stack: normalizeString(event.error && event.error.stack),
        source: normalizeString(event.filename),
        line: Number.isFinite(event.lineno) ? event.lineno : null,
        column: Number.isFinite(event.colno) ? event.colno : null,
    });
};

const handleUnhandledRejection = (event) => {
    const reason = event ? event.reason : null;
    reportClientError({
        kind: 'promise',
        errorType: normalizeString(reason && reason.name) || 'UnhandledPromiseRejection',
        title: 'Unhandled promise rejection',
        message: stringifyReason(reason) || 'Unhandled promise rejection',
        stack: normalizeString(reason && reason.stack),
    });
};

export const installGlobalErrorReporting = () => {
    if (typeof window === 'undefined') {
        return;
    }

    if (window.__litium && window.__litium.errorReportingInstalled) {
        return;
    }

    window.__litium = window.__litium || {};
    window.__litium.errorReportingInstalled = true;
    window.__litium.reportHttpError = reportHttpError;

    window.addEventListener('error', handleWindowError, true);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);
};
